import type { Notification } from '../model/types'

/** Сколько прочитанных id помним на пользователя; старые вытесняются. */
export const READ_IDS_LIMIT = 500

const STORAGE_KEY_PREFIX = 'femo:notifications:read:'

/** Ключ хранилища — с ролью и id: у админа, участника и представителя id могут совпасть. */
export function readIdsStorageKey(role: string, userId: number): string {
  return `${STORAGE_KEY_PREFIX}${role}:${userId}`
}

/** Прочитанные id пользователя. Хранилище закрыто или данные битые — пусто (всё новое), без исключений. */
export function loadReadIds(role: string | null, userId: number | null): number[] {
  if (userId == null || role == null) return []
  try {
    const raw = localStorage.getItem(readIdsStorageKey(role, userId))
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    return parsed.filter((id): id is number => Number.isInteger(id) && id >= 0)
  } catch {
    return []
  }
}

/** Пишет прочитанные id. false — записать не удалось (нет пользователя или хранилище закрыто). */
export function saveReadIds(
  role: string | null,
  userId: number | null,
  ids: readonly number[]
): boolean {
  if (userId == null || role == null) return false
  try {
    localStorage.setItem(readIdsStorageKey(role, userId), JSON.stringify(ids))
    return true
  } catch {
    return false
  }
}

/** Дописывает новые id в конец без повторов и обрезает по лимиту с головы (старые вытесняются). */
export function mergeReadIds(
  prev: readonly number[],
  add: readonly number[],
  limit: number = READ_IDS_LIMIT
): number[] {
  const added = [...new Set(add)]
  const addedSet = new Set(added)
  return [...prev.filter((id) => !addedSet.has(id)), ...added].slice(-limit)
}

/** Отпечаток уведомления: по нему прочитанность живого пуша (id < 0) переезжает на строку снимка.
 *  Без `created_at`: у живого пуша там клиентское время, у строки снимка — серверное. */
export function notificationFingerprint(n: Notification): string {
  return JSON.stringify([n.type_display, n.title, n.message ?? ''])
}

/** Заявка, которая ждёт решения: остаётся новой до approve/decline. */
export function isUnresolvedRequest(n: Notification): boolean {
  return !!n.actionable && !n.localStatus
}

/** Новое ли уведомление. Решённая заявка прочитана всегда. */
export function isNotificationNew(
  n: Notification,
  readIds: ReadonlySet<number>,
  readFingerprints: ReadonlySet<string>
): boolean {
  if (n.localStatus) return false
  if (n.id >= 0) return !readIds.has(n.id)
  return !readFingerprints.has(notificationFingerprint(n))
}

/** Что отметить прочитанным при закрытии окна: всё, кроме нерешённых заявок.
 *  Заявка из `resolvedIds` (решена другим админом, кнопки скрыты) — тоже прочитана.
 *  Постоянные id идут в хранилище, временные (id < 0) — отпечатками (по одному на строку). */
export function selectToMarkRead(
  list: readonly Notification[],
  resolvedIds: ReadonlySet<number> = new Set()
): {
  ids: number[]
  fingerprints: string[]
} {
  const ids: number[] = []
  const fingerprints: string[] = []
  for (const n of list) {
    if (isUnresolvedRequest(n) && !resolvedIds.has(n.id)) continue
    if (n.id >= 0) ids.push(n.id)
    else fingerprints.push(notificationFingerprint(n))
  }
  return { ids, fingerprints }
}

/** Строки снимка, чей отпечаток совпал с прочитанным живым пушем: их id надо запомнить.
 *  Один прочитанный отпечаток переносится на одну строку и потребляется (`consumed`).
 *  Нерешённая заявка и уже прочитанная строка не берут отпечаток. */
export function matchFingerprints(
  list: readonly Notification[],
  fingerprints: readonly string[],
  readIds: ReadonlySet<number> = new Set()
): { ids: number[]; consumed: string[] } {
  if (fingerprints.length === 0) return { ids: [], consumed: [] }
  const left = [...fingerprints]
  const ids: number[] = []
  const consumed: string[] = []
  for (const n of list) {
    if (n.id < 0 || isUnresolvedRequest(n) || readIds.has(n.id)) continue
    const fp = notificationFingerprint(n)
    const at = left.indexOf(fp)
    if (at < 0) continue
    left.splice(at, 1)
    ids.push(n.id)
    consumed.push(fp)
  }
  return { ids, consumed }
}
